import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import pricingService from '../services/pricingService';
import '../styles/PricingModal.css';

const PricingModal = ({ isOpen, onClose, currentTier = 'free', onUpgrade }) => {
  const [pricingTiers, setPricingTiers] = useState({});
  const [loading, setLoading] = useState(false);
  const [userUsage, setUserUsage] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadPricingData();
    }
  }, [isOpen]);

  const loadPricingData = async () => {
    try {
      setLoading(true);
      
      // Load pricing tiers
      const tiers = await pricingService.getPricingTiers();
      setPricingTiers(tiers);
      
      // Load user usage if authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        const usage = await pricingService.getUserUsage(session.access_token);
        setUserUsage(usage);
      }
    } catch (error) {
      console.error('Error loading pricing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (tierId) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      alert('Please sign in to upgrade your plan');
      return;
    }

    try {
      setLoading(true);
      setSelectedTier(tierId);

      const idToken = session.access_token;
      const { checkoutUrl } = await pricingService.createUpgradeSession(idToken, tierId);
      
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
      
    } catch (error) {
      console.error('Error creating upgrade session:', error);
      alert('Failed to start upgrade process. Please try again.');
    } finally {
      setLoading(false);
      setSelectedTier(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pricing-modal-overlay" onClick={onClose}>
      <div className="pricing-modal" onClick={e => e.stopPropagation()}>
        <div className="pricing-modal-header">
          <h2>Choose Your Plan</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        {userUsage && (
          <div className="current-usage">
            <h3>Current Usage</h3>
            <div className="usage-stats">
              <div className="usage-stat">
                <span className="stat-label">Plan:</span>
                <span className="stat-value">
                  {pricingService.getTierBadge(userUsage.tier)} {userUsage.tier.charAt(0).toUpperCase() + userUsage.tier.slice(1)}
                </span>
              </div>
              <div className="usage-stat">
                <span className="stat-label">Used:</span>
                <span className="stat-value">{userUsage.current} / {userUsage.limit === -1 ? '∞' : userUsage.limit}</span>
              </div>
              <div className="usage-stat">
                <span className="stat-label">Remaining:</span>
                <span className="stat-value">{userUsage.remaining === -1 ? '∞' : userUsage.remaining}</span>
              </div>
            </div>
          </div>
        )}

        <div className="pricing-tiers">
          {Object.entries(pricingTiers).map(([tierId, tier]) => (
            <div 
              key={tierId} 
              className={`pricing-tier ${currentTier === tierId ? 'current' : ''}`}
            >
              <div className="tier-header">
                <h3>
                  {pricingService.getTierBadge(tierId)} {tier.name}
                </h3>
                <div className="tier-price">
                  ${tier.price}
                  <span className="price-period">
                    {tierId === 'free' ? '' : '/month'}
                  </span>
                </div>
              </div>
              
              <div className="tier-features">
                {tierId === 'free' && (
                  <>
                    <div className="feature">✓ {tier.features.analysesPerPeriod} analyses every {tier.features.resetIntervalHours} hours</div>
                    <div className="feature">✓ Up to {tier.features.maxImagesPerAnalysis} images per analysis</div>
                    <div className="feature">✓ Component breakdown</div>
                    <div className="feature">✓ Price research</div>
                    <div className="feature">✓ CSV export</div>
                    <div className="feature">✓ Community support</div>
                  </>
                )}
                
                {(tierId === 'premium' || tierId === 'pro') && (
                  <>
                    <div className="feature">✓ {tier.features.analysesPerDay} analyses per day</div>
                    <div className="feature">✓ Up to {tier.features.maxImagesPerAnalysis} images per analysis</div>
                    <div className="feature">✓ Advanced component breakdown</div>
                    <div className="feature">✓ Premium price research</div>
                    <div className="feature">✓ CSV export</div>
                    <div className="feature">✓ Priority email support</div>
                    {tierId === 'pro' && (
                      <div className="feature">✓ Advanced features</div>
                    )}
                  </>
                )}
              </div>
              
              <div className="tier-action">
                {currentTier === tierId ? (
                  <button className="current-plan-button" disabled>
                    Current Plan
                  </button>
                ) : tierId === 'free' ? (
                  <button className="downgrade-button" disabled>
                    Basic Plan
                  </button>
                ) : (
                  <button 
                    className="upgrade-button"
                    onClick={() => handleUpgrade(tierId)}
                    disabled={loading}
                  >
                    {loading && selectedTier === tierId ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      'Upgrade'
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="pricing-footer">
          <p>All plans include secure processing and data privacy protection.</p>
          <p>You can upgrade or downgrade at any time.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;